// src/pages/hr/ApprovalsPage.jsx
//
// HR -> Approvals Page
// ====================
//
// PURPOSE
// -------
// This page provides a dedicated "approval inbox" for leave requests.
//
// Why this page exists even though Dashboard already shows work items:
// - Dashboard is a mixed inbox (ToDos + leave workflow items).
// - This page is focused specifically on leave approvals.
// - It gives the approver a cleaner operational view.
//
// CURRENT ARCHITECTURE ALIGNMENT
// ------------------------------
// 1) HTTP is authoritative.
//    - We load data from GET /leave-requests/inbox.
//    - We do not derive approval state on the client.
//
// 2) apiClient owns refresh/session recovery.
//    - This page does NOT call refresh itself.
//    - It only reacts to auth failures.
//
// 3) Multi-tab-safe auth policy.
//    - On 401 / session ambiguity / refresh failure, we call softLogout()
//      instead of hard logout.
//    - Hard logout would clear shared cookies and affect other tabs.
//
// IMPORTANT ROLE NOTE
// -------------------
// We intentionally do NOT block this page only to "superuser/admin" on the frontend.
//
// Why?
// - Backend inbox endpoint already applies the real access policy.
// - A normal user may still be a valid assigned approver.
// - Frontend should not hide a legitimate workflow route based on incomplete role assumptions.
//
// So the page behavior is:
// - authenticated user -> try loading
// - backend decides whether data is available / forbidden
//
// UX
// --
// - optional date filters
// - approve / reject actions
// - open canonical work item details
//
// NOTE
// ----
// Reject currently uses window.prompt() for a quick note.
// This is acceptable for now, but later a proper modal would be a better UX.

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AuthContext from "../../context/AuthContext";
import {
  fetchApproverInboxFiltered,
  decideLeaveRequest,
} from "../../services/leaveRequestsService";
import { getTranslatedApiErrorMessage, isRequestCanceled } from "../../services/httpUtils";
import { formatLeaveBusinessEnd, formatLeaveBusinessStart } from "../../utils/leaveDateUtils";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

function isAuthFailure(err) {
  const status = err?.response?.status;
  const code = err?.code;

  return (
    status === 401 ||
    code === "SESSION_ID_REQUIRED" ||
    code === "REFRESH_FAILED"
  );
}

function requesterLabel(row) {
  if (row?.user) {
    const first = row.user.first_name || "";
    const last = row.user.last_name || "";
    const full = `${first} ${last}`.trim();
    const username = row.user.username ? ` (${row.user.username})` : "";
    return full ? `${full}${username}` : row.user.username || `User #${row.user.id}`;
  }

  if (row?.user_id != null) return `User #${row.user_id}`;
  return "—";
}

function leaveTypeLabel(row) {
  return row?.leaveType?.name_pl || row?.leaveType?.name_en || row?.leaveType?.code || "—";
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function ApprovalsPage() {
  const { t } = useTranslation(["common", "leaves"]);
  const navigate = useNavigate();
  const { authState, authChecked, softLogout } = useContext(AuthContext);

  const isMountedRef = useRef(false);
  const abortRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Filters                                                                   */
  /* ------------------------------------------------------------------------ */

  /**
   * Inbox endpoint is already "pending approvals",
   * but we still pass status=PENDING to keep filtering explicit and readable.
   */
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filters = useMemo(
    () => ({
      from: from || null,
      to: to || null,
      status: "PENDING",
    }),
    [from, to]
  );

  const hasInvalidDateRange = Boolean(from && to && from > to);

  /* ------------------------------------------------------------------------ */
  /* Data state                                                                */
  /* ------------------------------------------------------------------------ */

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);

  /* ------------------------------------------------------------------------ */
  /* Load data                                                                 */
  /* ------------------------------------------------------------------------ */

  const loadApprovals = useCallback(
    async ({ keepLoading = false } = {}) => {
      if (!authChecked || !authState?.isAuthenticated) return;

      if (hasInvalidDateRange) {
        setError(t("leaves:approvals.invalidDateRange"));
        setRows([]);
        setLoading(false);
        return;
      }

      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;

      if (!keepLoading) {
        setLoading(true);
      }

      setError("");
      setForbidden(false);

      try {
        const list = await fetchApproverInboxFiltered(filters, {
          signal: controller.signal,
        });

        if (controller.signal.aborted || !isMountedRef.current) return;

        const sorted = [...list].sort((a, b) => {
          const aT = new Date(a.createdAt || a.start_at || 0).getTime();
          const bT = new Date(b.createdAt || b.start_at || 0).getTime();
          return bT - aT;
        });

        setRows(sorted);
      } catch (err) {
        if (isRequestCanceled(err) || controller.signal.aborted) return;

        if (isAuthFailure(err)) {
          softLogout("APPROVALS_PAGE_AUTH_FAILURE");
          return;
        }

        if (err?.response?.status === 403) {
          if (!isMountedRef.current) return;
          setForbidden(true);
          setRows([]);
          return;
        }

        if (DEV) {
          // eslint-disable-next-line no-console
          console.error("[ApprovalsPage] load failed:", err);
        }

        if (!isMountedRef.current) return;
        setError(getTranslatedApiErrorMessage(err, t, t("leaves:approvals.loadError")));
      } finally {
        if (!controller.signal.aborted && isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [authChecked, authState?.isAuthenticated, filters, hasInvalidDateRange, softLogout, t]
  );

  useEffect(() => {
    if (!authChecked || !authState?.isAuthenticated) return;
    loadApprovals();

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [authChecked, authState?.isAuthenticated, loadApprovals]);

  /* ------------------------------------------------------------------------ */
  /* Navigation                                                                 */
  /* ------------------------------------------------------------------------ */

  const openWorkItem = useCallback(
    (row) => {
      const key = row?.work_item_key || (row?.id ? `leave:${row.id}` : null);
      if (!key) return;

      navigate(`/work-items/${encodeURIComponent(key)}`);
    },
    [navigate]
  );

  /* ------------------------------------------------------------------------ */
  /* Decisions                                                                  */
  /* ------------------------------------------------------------------------ */

  const doDecision = useCallback(
    async (row, decision) => {
      if (!row?.id) return;

      const note =
        decision === "REJECTED"
          ? window.prompt(t("leaves:approvals.rejectNotePrompt"), "") || null
          : null;

      setBusyId(String(row.id));
      setError("");

      try {
        await decideLeaveRequest(row.id, decision, note);

        // Authoritative refresh after successful decision.
        // This avoids subtle drift if backend mutates related items as well.
        await loadApprovals({ keepLoading: true });
      } catch (err) {
        if (isAuthFailure(err)) {
          softLogout("APPROVALS_PAGE_DECISION_AUTH_FAILURE");
          return;
        }

        if (DEV) {
          // eslint-disable-next-line no-console
          console.error("[ApprovalsPage] decision failed:", err);
        }

        if (!isMountedRef.current) return;
        setError(getTranslatedApiErrorMessage(err, t, t("leaves:approvals.decisionError")));
      } finally {
        if (isMountedRef.current) {
          setBusyId(null);
        }
      }
    },
    [loadApprovals, softLogout, t]
  );

  /* ------------------------------------------------------------------------ */
  /* Render gating                                                             */
  /* ------------------------------------------------------------------------ */

  if (!authChecked) {
    return (
      <div style={{ padding: 18 }}>
        <h2 style={{ marginTop: 0 }}>{t("leaves:approvals.pageTitle")}</h2>
        <div style={{ color: "#6b7280" }}>{t("common:common.loading")}</div>
      </div>
    );
  }

  if (!authState?.isAuthenticated) {
    return (
      <div style={{ padding: 18 }}>
        <h2 style={{ marginTop: 0 }}>{t("leaves:approvals.pageTitle")}</h2>
        <div style={{ color: "#6b7280" }}>{t("leaves:approvals.notAuthenticated")}</div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Render                                                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div style={{ padding: 18 }}>
      <h2 style={{ marginTop: 0 }}>{t("leaves:approvals.pageTitle")}</h2>

      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
        {t("leaves:approvals.description")}
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "end",
          marginBottom: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>{t("leaves:common.from")}</div>
          <input
            className="field-input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>{t("leaves:common.to")}</div>
          <input
            className="field-input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setFrom("");
            setTo("");
          }}
        >
          {t("leaves:approvals.clearFilters")}
        </button>

        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => loadApprovals()}
          disabled={loading}
        >
          {t("common:actions.refresh")}
        </button>

        <div style={{ marginLeft: "auto", color: "#6b7280", fontSize: 13 }}>
          {loading ? t("common:common.loading") : t("leaves:approvals.pendingCount", { count: rows.length })}
        </div>
      </div>

      {error && (
        <div className="msg error" style={{ marginBottom: 10 }}>
          {error}
        </div>
      )}

      {forbidden && (
        <div className="msg error" style={{ marginBottom: 10 }}>
          {t("leaves:approvals.permissionDenied")}
        </div>
      )}

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, background: "#fff" }}>
        <div
          style={{
            padding: 12,
            borderBottom: "1px solid #e5e7eb",
            fontWeight: 900,
          }}
        >
          {t("leaves:approvals.tableTitle")}
        </div>

        {loading ? (
          <div style={{ padding: 12, color: "#6b7280" }}>{t("common:common.loading")}</div>
        ) : forbidden ? (
          <div style={{ padding: 12, color: "#6b7280" }}>
            {t("leaves:approvals.restrictedByPolicy")}
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 12, color: "#6b7280" }}>{t("leaves:approvals.nothingPending")}</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", fontSize: 12, color: "#6b7280" }}>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:approvals.requester")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.type")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.start")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:common.end")}</th>
                  <th style={{ padding: 10, borderBottom: "1px solid #e5e7eb" }}>{t("leaves:leaveTypes.headers.actions")}</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row) => {
                  const busy = String(busyId || "") === String(row.id);

                  return (
                    <tr key={row.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: 10, fontWeight: 800 }}>{requesterLabel(row)}</td>

                      <td style={{ padding: 10, fontWeight: 800 }}>{leaveTypeLabel(row)}</td>

                      <td style={{ padding: 10 }}>{formatLeaveBusinessStart(row)}</td>
                      <td style={{ padding: 10 }}>{formatLeaveBusinessEnd(row)}</td>

                      <td style={{ padding: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={() => openWorkItem(row)}
                          disabled={busy}
                        >
                          {t("leaves:common.open")}
                        </button>

                        <button
                          className="btn btn-primary"
                          type="button"
                          disabled={busy}
                          onClick={() => doDecision(row, "APPROVED")}
                        >
                          {busy ? t("leaves:approvals.working") : t("leaves:workItem.approve")}
                        </button>

                        <button
                          className="btn btn-secondary"
                          type="button"
                          disabled={busy}
                          onClick={() => doDecision(row, "REJECTED")}
                        >
                          {busy ? t("leaves:approvals.working") : t("leaves:workItem.reject")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

